import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box, Typography, Paper, TextField, Button, Grid, Alert, Chip, FormControl, InputLabel, Select, OutlinedInput, MenuItem
} from '@mui/material';
import { CameraAlt, CloudUpload } from '@mui/icons-material';
import { createSample } from '../store/sampleSlice';
import api from '../services/api';

// Use mobile-friendly scanner
import MobileCameraScanner from '../components/MobileCameraScanner';
import BulkUpload from '../components/BulkUpload';

const SampleSchema = Yup.object().shape({
  labId: Yup.string()
    .required('Lab ID is required')
    .min(3, 'Lab ID must be at least 3 characters'),
  sampleName: Yup.string()
    .required('Sample name is required')
    .min(2, 'Sample name must be at least 2 characters'),
  storageTemperature: Yup.string()
    .required('Storage temperature is required'),
  regularity: Yup.string()
    .required('Regularity is required'),
  departments: Yup.array()
    .min(1, 'At least one department must be selected')
    .required('Departments are required')
});

const CreateSample = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [formikRef, setFormikRef] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({});

  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  const fetchDropdownOptions = async () => {
    try {
      const response = await api.get('/dropdowns');
      setDropdownOptions(response.data.data);
    } catch (error) {
      console.error('Failed to fetch dropdown options:', error);
    }
  };
  


  if (!['Admin', 'Sample Collector'].includes(user?.role)) {
    return (
      <Box>
        <Alert severity="error">Access denied. Only Admin and Sample Collector can create samples.</Alert>
      </Box>
    );
  }

  const handleSubmit = async (values, { setFieldError }) => {
    setSubmitError('');
    setIsSubmitting(true);
    
    const sampleData = {
      labId: values.labId.trim(),
      sampleName: values.sampleName.trim(),
      storageTemperature: values.storageTemperature,
      regularity: values.regularity,
      departments: values.departments
    };

    if (values.regularity === 'BIS' && values.bisSubcategories?.length > 0) {
      sampleData.bisSubcategories = values.bisSubcategories;
    }

    if (values.regularity === 'FSSAI' && values.fssaiCategory) {
      sampleData.fssaiCategory = values.fssaiCategory;
    }

    try {
      await dispatch(createSample(sampleData)).unwrap();
      navigate('/samples');
    } catch (error) {
      setIsSubmitting(false);
      const errorMessage = error.message || 'Failed to create sample';
      
      if (errorMessage.includes('Lab ID') && errorMessage.includes('already exists')) {
        setFieldError('labId', errorMessage);
      } else {
        setSubmitError(errorMessage);
      }
    }
  };

  const handleScannerData = (scannedData) => {
    if (formikRef) {
      // Map scanned data to form fields
      formikRef.setFieldValue('labId', scannedData.sampleCode || '');
      formikRef.setFieldValue('sampleName', scannedData.sampleName || '');
      
      // Convert receiptDate to createdDate if needed
      if (scannedData.receiptDate) {
        // You can set this in backend or handle date conversion here
        console.log('Receipt Date:', scannedData.receiptDate);
      }
    }
    setScannerOpen(false);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Create New Sample</Typography>
      
      <Paper sx={{ p: 3 }}>
        <Formik
          initialValues={{
            labId: '',
            sampleName: '',
            storageTemperature: '',
            regularity: '',
            bisSubcategories: [],
            fssaiCategory: '',
            departments: []
          }}
          validationSchema={SampleSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isValid, dirty, values, setFieldValue, ...formikProps }) => {
            // Store formik reference for scanner callback
            if (!formikRef) {
              setFormikRef({ setFieldValue, ...formikProps });
            }
            return (
            <Form>
              {submitError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {submitError}
                </Alert>
              )}
              
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Sample Information</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    onClick={() => setBulkUploadOpen(true)}
                    sx={{ 
                      minWidth: { xs: 'auto', sm: '140px' },
                      '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Bulk Upload</Box>
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CameraAlt />}
                    onClick={() => setScannerOpen(true)}
                    sx={{ 
                      minWidth: { xs: 'auto', sm: '140px' },
                      '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>📷 Scan Tag</Box>
                  </Button>
                </Box>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="labId"
                    label="Lab ID *"
                    fullWidth
                    error={touched.labId && errors.labId}
                    helperText={touched.labId && errors.labId || 'Unique identifier for the sample'}
                    placeholder="e.g., LAB-2024-001"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="sampleName"
                    label="Sample Name *"
                    fullWidth
                    error={touched.sampleName && errors.sampleName}
                    helperText={touched.sampleName && errors.sampleName}
                    placeholder="e.g., Water Quality Sample"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth error={touched.departments && errors.departments}>
                    <InputLabel>Departments *</InputLabel>
                    <Select
                      multiple
                      value={values.departments}
                      onChange={(e) => setFieldValue('departments', e.target.value)}
                      input={<OutlinedInput label="Departments *" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {(dropdownOptions.departments || []).map((dept) => (
                        <MenuItem key={dept.value} value={dept.value}>{dept.label}</MenuItem>
                      ))}
                    </Select>
                    {touched.departments && errors.departments && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.departments}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={touched.storageTemperature && errors.storageTemperature}>
                    <InputLabel>Storage Temperature *</InputLabel>
                    <Select
                      value={values.storageTemperature}
                      onChange={(e) => setFieldValue('storageTemperature', e.target.value)}
                      input={<OutlinedInput label="Storage Temperature *" />}
                    >
                      {(dropdownOptions.storageTemperature || []).map((temp) => (
                        <MenuItem key={temp.value} value={temp.value}>{temp.label}</MenuItem>
                      ))}
                    </Select>
                    {touched.storageTemperature && errors.storageTemperature && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.storageTemperature}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={touched.regularity && errors.regularity}>
                    <InputLabel>Regularity *</InputLabel>
                    <Select
                      value={values.regularity}
                      onChange={(e) => {
                        setFieldValue('regularity', e.target.value);
                        setFieldValue('bisSubcategories', []);
                        setFieldValue('fssaiCategory', '');
                      }}
                      input={<OutlinedInput label="Regularity *" />}
                    >
                      {(dropdownOptions.regularity || []).map((reg) => (
                        <MenuItem key={reg.value} value={reg.value}>{reg.label}</MenuItem>
                      ))}
                    </Select>
                    {touched.regularity && errors.regularity && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.regularity}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                {values.regularity === 'BIS' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>BIS Subcategories</InputLabel>
                      <Select
                        multiple
                        value={values.bisSubcategories}
                        onChange={(e) => setFieldValue('bisSubcategories', e.target.value)}
                        input={<OutlinedInput label="BIS Subcategories" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {(dropdownOptions.bisSubcategories || []).map((sub) => (
                          <MenuItem key={sub.value} value={sub.value}>{sub.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {values.regularity === 'FSSAI' && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>FSSAI Category</InputLabel>
                      <Select
                        value={values.fssaiCategory}
                        onChange={(e) => setFieldValue('fssaiCategory', e.target.value)}
                        input={<OutlinedInput label="FSSAI Category" />}
                      >
                        {(dropdownOptions.fssaiCategory || []).map((cat) => (
                          <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button 
                  type="submit" 
                  variant="contained"
                  disabled={isSubmitting || (
                    !values.labId.trim() || 
                    !values.sampleName.trim() || 
                    !values.storageTemperature || 
                    !values.regularity || 
                    values.departments.length === 0
                  )}
                >
                  {isSubmitting ? 'Creating...' : 'Create Sample'}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/samples')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </Box>
            </Form>
            );
          }}
        </Formik>
        
        {/* Scanner component - always rendered */}
        <MobileCameraScanner
          open={scannerOpen}
          onClose={() => {
            console.log('Scanner closed');
            setScannerOpen(false);
          }}
          onDataExtracted={(data) => {
            console.log('Data extracted:', data);
            handleScannerData(data);
          }}
        />
        
        {/* Bulk Upload Dialog */}
        <BulkUpload
          open={bulkUploadOpen}
          onClose={() => setBulkUploadOpen(false)}
          onSuccess={() => {
            setBulkUploadOpen(false);
            navigate('/samples');
          }}
        />
      </Paper>
    </Box>
  );
};

export default CreateSample;